import React, { useEffect, useState } from 'react';
import {
	Stepper as MuiStepper,
	Step,
	StepLabel,
	Typography,
	StepConnector,
	stepConnectorClasses,
	Box,
	Paper,
	Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
	Check, 
	RadioButtonUnchecked, 
	PlayArrow,
	Schedule,
	CheckCircle,
	Error
} from '@mui/icons-material';
import { STEPS, DARK_MODE, JLR_PLANT } from '../../constants';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

// Compact Horizontal Timeline Container
const CompactTimelineContainer = styled(Box)(({ theme, themeMode, isJlr }) => ({
	display: 'flex',
	alignItems: 'center',
	padding: isJlr ? '12px 20px' : '8px 16px',
	borderRadius: isJlr ? '16px' : '8px',
	background: isJlr 
		? (themeMode === DARK_MODE 
			? 'rgba(26, 45, 26, 0.3)'
			: 'rgba(248, 249, 250, 0.8)')
		: (themeMode === DARK_MODE ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
	backdropFilter: isJlr ? 'blur(10px)' : 'none',
	border: isJlr 
		? `1px solid ${themeMode === DARK_MODE ? 'rgba(255, 255, 255, 0.1)' : 'rgba(45, 74, 45, 0.1)'}`
		: `1px solid ${theme.palette.divider}`,
	marginBottom: '16px',
	transition: 'all 0.3s ease',
	maxWidth: '100%',
	overflow: 'hidden',
}));

// Compact Step Item
const CompactStep = styled(Box)(({ theme, themeMode, isJlr, status }) => {
	return {
		display: 'flex',
		alignItems: 'center',
		marginRight: '16px',
		minWidth: 'fit-content',
		
		'&:last-child': {
			marginRight: 0,
		},
	};
});

// Small Step Icon
const SmallStepIcon = styled(Box)(({ theme, themeMode, isJlr, status }) => {
	const getColors = () => {
		switch (status) {
			case 'completed': 
				return {
					bg: '#27ae60',
					color: '#ffffff'
				};
			case 'active': 
				return {
					bg: isJlr ? '#2d4a2d' : '#82C1A7', // JLR green instead of blue
					color: '#ffffff'
				};
			case 'pending': 
			default:
				return {
					bg: themeMode === DARK_MODE ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
					color: themeMode === DARK_MODE ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'
				};
		}
	};

	const colors = getColors();

	return {
		width: '28px',
		height: '28px',
		borderRadius: '50%',
		background: colors.bg,
		color: colors.color,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginRight: '8px',
		fontSize: '14px',
		fontWeight: 600,
		border: status === 'pending' ? `2px solid ${colors.bg}` : 'none',
		transition: 'all 0.3s ease',
		flexShrink: 0,
	};
});

// Step Connector Line (Horizontal)
const HorizontalConnector = styled(Box)(({ theme, themeMode, isCompleted }) => ({
	width: '24px',
	height: '2px',
	background: isCompleted 
		? '#27ae60'
		: (themeMode === DARK_MODE ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'),
	marginRight: '8px',
	transition: 'all 0.3s ease',
	flexShrink: 0,
}));

// Compact Status Badge
const CompactStatusBadge = styled(Box)(({ theme, themeMode, isJlr, status }) => {
	const getStatusStyles = () => {
		switch (status) {
			case 'completed':
				return {
					color: '#27ae60',
					text: 'COMPLETED',
				};
			case 'active':
				return {
					color: isJlr ? '#2d4a2d' : '#82C1A7',
					text: 'ACTIVE',
				};
			case 'pending':
			default:
				return {
					color: themeMode === DARK_MODE ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
					text: 'PENDING',
				};
		}
	};

	const statusStyles = getStatusStyles();

	return {
		fontSize: '10px',
		fontWeight: 600,
		color: statusStyles.color,
		fontFamily: isJlr ? "'Inter', sans-serif" : theme.typography.fontFamily,
		letterSpacing: '0.5px',
		marginTop: '2px',
	};
});

const mapStateToProps = (state) => {
	return {
		allowedOperations: state.operations.allowedOperations,
		currentOperation: state.operations.currentOperation,
		themeMode: state.theme.themeMode,
		plantName: state.user.plant,
	};
};

const getActiveStep = (steps, currentOperation) =>
	steps.findIndex(({ operation }) => operation === currentOperation);

const Stepper = ({
	allowedOperations,
	currentOperation,
	themeMode,
	plantName,
	children,
	...otherProps
}) => {
	const { pathname } = useLocation();
	const isJlr = plantName === JLR_PLANT;

	const [allowedSteps, setAllowedSteps] = useState([]);
	const [activeStep, setActiveStep] = useState(0);

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const getStepStatus = (index) => {
		if (activeStep > index) return 'completed';
		if (activeStep === index) return 'active';
		return 'pending';
	};

	const getStepIcon = (status, stepNumber) => {
		switch (status) {
			case 'completed':
				return <CheckCircle sx={{ fontSize: '24px' }} />;
			case 'active':
				return <PlayArrow sx={{ fontSize: '24px' }} />;
			case 'pending':
			default:
				return stepNumber;
		}
	};

	useEffect(() => {
		const steps = [];
		allowedOperations.forEach((op) => {
			const step = STEPS.find((step) => step.operation === op);
			if (step) {
				steps.push(step);
			}
		});

		setAllowedSteps(steps);
	}, [allowedOperations, pathname]);

	useEffect(() => {
		if (allowedSteps.length) {
			setActiveStep(getActiveStep(allowedSteps, currentOperation));
		}
	}, [allowedSteps, currentOperation]);

	return (
		<>
			{/* <CompactTimelineContainer themeMode={themeMode} isJlr={isJlr}> */}
				{/* Progress indicator */}
				{/* <Typography 
					variant="caption" 
					sx={{ 
						fontFamily: isJlr ? "'Inter', sans-serif" : 'inherit',
						fontWeight: 600,
						color: 'text.secondary',
						mr: 2,
						minWidth: 'fit-content'
					}}
				>
					{activeStep + 1}/{allowedSteps.length}
				</Typography> */}

				{/* Horizontal Timeline Steps */}
				{/* {allowedSteps.map(({ label }, index) => {
					const status = getStepStatus(index);
					const isLast = index === allowedSteps.length - 1; */}

					{/* // return (
					// 	<React.Fragment key={label}> */}
							{/* <CompactStep themeMode={themeMode} isJlr={isJlr} status={status}>
								Step Icon */}
								{/* <SmallStepIcon themeMode={themeMode} isJlr={isJlr} status={status}>
									{status === 'completed' ? (
										<Check sx={{ fontSize: '16px' }} />
									) : status === 'active' ? (
										<PlayArrow sx={{ fontSize: '16px' }} />
									) : (
										index + 1
									)}
								</SmallStepIcon> */}

								{/* Step Label */}
								{/* <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 'fit-content' }}>
									<Typography 
										variant="body2" 
										sx={{ 
											fontFamily: isJlr ? "'Inter', sans-serif" : 'inherit',
											fontWeight: isJlr ? 600 : 500,
											color: status === 'pending' ? 'text.disabled' : 'text.primary',
											fontSize: '12px',
											lineHeight: 1.2,
											whiteSpace: 'nowrap'
										}}
									> */}
										{/* {label.length > 15 ? label.substring(0, 12) + '...' : label}
									</Typography>
									<CompactStatusBadge themeMode={themeMode} isJlr={isJlr} status={status}>
										{status === 'completed' ? 'DONE' : 
										 status === 'active' ? 'ACTIVE' : 'PENDING'}
									</CompactStatusBadge>
								</Box> */}
							{/* </CompactStep> */}

							{/* Horizontal Connector */}
							{/* {!isLast && (
								<HorizontalConnector 
									themeMode={themeMode} 
									isCompleted={status === 'completed'}
								/>
							)} */}
						{/* </React.Fragment>
					);
				})} */}

				{/* Overall Progress */}
				{/* <Box sx={{ 
					ml: 'auto', 
					display: 'flex', 
					alignItems: 'center',
					minWidth: 'fit-content'
				}}>
					<Typography 
						variant="caption" 
						sx={{ 
							fontFamily: isJlr ? "'Inter', sans-serif" : 'inherit',
							color: 'text.secondary',
							mr: 1
						}}
					>
						{Math.round(((activeStep + 1) / allowedSteps.length) * 100)}%
					</Typography>
					<Box sx={{
						width: '60px',
						height: '4px',
						borderRadius: '2px',
						backgroundColor: themeMode === DARK_MODE ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
						overflow: 'hidden'
					}}>
						<Box sx={{
							width: `${((activeStep + 1) / allowedSteps.length) * 100}%`,
							height: '100%',
							background: isJlr ? '#2d4a2d' : '#82C1A7',
							transition: 'width 0.5s ease',
							borderRadius: '2px'
						}} />
					</Box>
				</Box>
			</CompactTimelineContainer> */}
			
			{children(activeStep, handleNext)}
		</>
	);
};

export default connect(mapStateToProps)(Stepper);
